import { cloneElement, isValidElement, type ReactElement } from 'react';
import styled from 'styled-components';
import { Info, X } from '@phosphor-icons/react';
import { ConversionUnitViewer, ConversionViewer, DarkPopup } from '../../components';
import { BoldText, MuteText } from '../../elements';
import { calculationToolTips, CommonUtility, theme } from '../../utility';

// ========== Styled Components ==========
const CBMContainer = styled.div<{ dark?: boolean }>`
  color: ${({ theme }) => theme.colors.grey};
  .value {
    color: ${({ theme, dark }) => (dark ? theme.colors.primary : theme.colors.grey)};
  }
`;

const Row = styled.div`
  justify-content: space-between;
  display: flex;
  margin-bottom: 5px;
  align-items: center;
`;

// ========== CBMPopupText ==========
interface ICBMPopupTextProps {
  imperial: boolean;
  cbm: number;
  cbf: number;
}

function CBMPopupText({ imperial, cbm, cbf }: ICBMPopupTextProps) {
  return (
    <div>
      <div>{imperial ? calculationToolTips.cbf : calculationToolTips.cbm}</div>
      <br />
      {imperial ? (
        <div>CBM = {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(cbm))}</div>
      ) : (
        <div>CBF = {CommonUtility.decimalWithCommas(CommonUtility.roundNumber(cbf))}</div>
      )}
    </div>
  );
}

// ========== CBMRenderer ==========
interface ICBMRendererProps {
  cbm: number;
  cbf: number;
  imperial: boolean;
  className?: string;
  dark?: boolean;
}

export function CBMRenderer({
  cbm,
  cbf,
  imperial,
  className,
  dark = false,
}: ICBMRendererProps) {
  return (
    <div className={`d-flex ${className ?? ''} align-items-center`}>
      <DarkPopup
        trigger={
          <CBMContainer className="d-flex align-items-center" dark={dark}>
            <span>{imperial ? 'CBF ' : 'CBM '}</span>
            <BoldText className="mx-1 value">
              {CommonUtility.roundNumber(imperial ? cbf : cbm)}
            </BoldText>
            <Info />
          </CBMContainer>
        }
      >
        <CBMPopupText imperial={imperial} cbm={cbm} cbf={cbf} />
      </DarkPopup>
    </div>
  );
}

// ========== calculateListTotal ==========
interface IItemAttribute {
  units_per_carton?: number;
  carton_gross_weight?: { value: number };
  [key: string]: any;
}

interface IAliasItem {
  attributes: IItemAttribute;
  qty: number;
}

interface IItem {
  qty?: number;
  price?: number;
  supplierItemCost?: number;
  supplierComponentCost?: number;
  attributes?: { attributes: IItemAttribute }[];
  aliases?: IAliasItem[];
}

interface IListTotal {
  cartons: number;
  units: number;
  retails: number;
  supplierValue: number;
  grossWeight: number;
}

export const calculateListTotal = (items: IItem[]): IListTotal => {
  const total: IListTotal = {
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  };

  items.forEach(item => {
    let latestAttribute =
      item.attributes?.find?.(x => CommonUtility.isValidObject(x.attributes))
        ?.attributes || ({} as IItemAttribute);

    const isMultiple = CommonUtility.isValidArray(item.aliases);

    if (isMultiple && item.aliases) {
      let cartons = 0;
      let unitsTotal = 0;
      item.aliases.forEach(aliasItem => {
        latestAttribute = aliasItem.attributes;
        const units = aliasItem.qty;
        const ctns = aliasItem.qty / (latestAttribute.units_per_carton || 1);

        if (ctns) {
          cartons += ctns;
          total.cartons += ctns;
          total.units += +units || 0;
          unitsTotal += +units || 0;
          total.grossWeight += ctns * (latestAttribute.carton_gross_weight?.value || 0);
        }
      });
      total.retails += (cartons || 0) * (item.price || 0);
      total.supplierValue +=
        (unitsTotal || 0) * (item.supplierItemCost || item.supplierComponentCost || 0);
    } else if (item.qty && latestAttribute) {
      const cartons = (item.qty || 0) / (latestAttribute.units_per_carton || 1);
      total.cartons += cartons;
      total.units += +item.qty || 0;
      total.retails += (+item.qty || 0) * (item.price || 0);
      total.supplierValue +=
        (+item.qty || 0) * (item.supplierItemCost || item.supplierComponentCost || 0);
      total.grossWeight += cartons * (latestAttribute.carton_gross_weight?.value || 0);
    }
  });

  return total;
};

// ========== OriginRow ==========
interface IOriginRowProps {
  origins?: string[];
}

export function OriginRow({ origins = [] }: IOriginRowProps) {
  return origins.length === 0 ? (
    <span />
  ) : origins.length > 1 ? (
    <DarkPopup trigger={<span>Multiple</span>} hoverable>
      <div>
        {origins.map(item => (
          <div key={item}>{item}</div>
        ))}
      </div>
    </DarkPopup>
  ) : (
    <span>{origins[0]}</span>
  );
}

// ========== ShipmentStatus ==========
export const ShipmentStatus = {
  reject: 'rejected',
  accept: 'accepted',
  draft: 'draft',
  preparing: 'preparing',
  receiving: 'receiving',
  completed: 'completed',
  in_transit: 'in_transit',
  ready_for_shipment: 'ready_for_shipment',
  deleted: 'deleted',
  shipped: 'shipped',
  prediction: 'prediction',
  DELETED: 'DELETED',
  cancelled: 'cancelled',
  CLOSED: 'CLOSED',
} as const;

// ========== ProductPropertiesConverted ==========
interface IProductPropertiesConvertedProps {
  attributes?: Record<string, any>;
  isMultiple?: boolean;
  AliasRenderer?: ReactElement;
  showAlias?: boolean;
  imperial?: boolean;
  unitKeys?: boolean;
}

export function ProductPropertiesConverted({
  attributes = {},
  isMultiple,
  AliasRenderer,
  showAlias = true,
  imperial = false,
  unitKeys = false,
}: IProductPropertiesConvertedProps) {
  const lengthKey = unitKeys ? 'length' : 'carton_length';
  const widthKey = unitKeys ? 'width' : 'carton_width';
  const heightKey = unitKeys ? 'height' : 'carton_height';
  const weightKey = unitKeys ? 'weight' : 'carton_gross_weight';

  return (
    <>
      {showAlias && (
        <Row>
          <MuteText>Alias</MuteText>
          <div className="text-right">
            {!attributes?.alias ? (
              '-'
            ) : (
              <span>
                {isValidElement(AliasRenderer)
                  ? cloneElement(AliasRenderer)
                  : isMultiple
                    ? 'Multiple'
                    : attributes.alias}
              </span>
            )}
          </div>
        </Row>
      )}

      <Row>
        <MuteText>Carton Size</MuteText>
        <div className="text-right">
          {!attributes?.[lengthKey] || isMultiple ? (
            <span>-</span>
          ) : (
            <div className="d-flex align-items-center">
              <ConversionViewer
                showUnit={false}
                value={attributes?.[lengthKey]?.value}
                imperial={imperial}
                originalUnit={attributes?.[lengthKey]?.unit}
              />
              <X color={theme.colors.grey} className="mx-1" />
              <ConversionViewer
                showUnit={false}
                value={attributes?.[widthKey]?.value}
                imperial={imperial}
                originalUnit={attributes?.[widthKey]?.unit}
              />
              <X color={theme.colors.grey} className="mx-1" />
              <ConversionViewer
                showUnit={false}
                value={attributes?.[heightKey]?.value}
                imperial={imperial}
                originalUnit={attributes?.[heightKey]?.unit}
              />
              <MuteText className="pl-1">
                <ConversionUnitViewer
                  originalUnit={attributes?.[heightKey]?.unit}
                  imperial={imperial}
                />
              </MuteText>
            </div>
          )}
        </div>
      </Row>

      {!unitKeys && (
        <Row>
          <MuteText>Quantity Per Carton</MuteText>
          <div className="text-right">{attributes?.units_per_carton}</div>
        </Row>
      )}

      <Row>
        <MuteText>{unitKeys ? 'Gross Weight' : 'Gross Weight (Carton)'}</MuteText>
        <div className="text-right">
          {isMultiple ? (
            '-'
          ) : (
            <ConversionViewer
              value={attributes?.[weightKey]?.value}
              imperial={imperial}
              originalUnit={attributes?.[weightKey]?.unit}
            />
          )}
        </div>
      </Row>
    </>
  );
}
